import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { IngressApplyPlan } from '@/api/types';

/**
 * Holds the most recent ingress plan that the current session has produced —
 * either via `POST /api/v1/access-manifests/apply` (Manifest workbench) or via
 * `POST /api/v1/ingress/apply-plan` (Ingress Plan page). Lets the Ingress page
 * pick up where the Manifest page left off without a re-request.
 *
 * Lives in the protected subtree (mounted by ProtectedRoute), so the value
 * resets when the user logs out or revisits.
 */
export interface IngressPlanContextValue {
  latestPlan: IngressApplyPlan | null;
  setLatestPlan: (plan: IngressApplyPlan | null) => void;
}

const IngressPlanContext = createContext<IngressPlanContextValue | null>(null);

export function IngressPlanProvider({ children }: { children: ReactNode }) {
  const [latestPlan, setLatestPlanState] = useState<IngressApplyPlan | null>(null);
  const setLatestPlan = useCallback((plan: IngressApplyPlan | null) => {
    setLatestPlanState(plan);
  }, []);
  const value = useMemo<IngressPlanContextValue>(() => ({ latestPlan, setLatestPlan }), [
    latestPlan,
    setLatestPlan,
  ]);
  return <IngressPlanContext.Provider value={value}>{children}</IngressPlanContext.Provider>;
}

export function useIngressPlan(): IngressPlanContextValue {
  const ctx = useContext(IngressPlanContext);
  if (ctx) return ctx;
  // Outside the protected subtree (e.g. /login) — return a noop so leaf
  // components do not need to special-case the public routes.
  return { latestPlan: null, setLatestPlan: () => {} };
}
